import type { IEnv, ILog } from '@monofe/util';
import type { Middleware } from '@reduxjs/toolkit';
import type { IStoreMiddlewareMap, IStoreMiddlewares } from '../interface';
import { IStoreMiddlewareType } from '../interface';
import effect from './effect';
import interceptor from './interceptor';
import logger from './logger';

const buildMiddlewares = (log: ILog, env: IEnv): IStoreMiddlewares => {
    const isProduction = env.getString('NODE_ENV', 'production') === 'production';

    const middlewareLogger = !isProduction ? logger(log) : undefined;
    const middlewareEffect = effect();

    const middlewareInterceptor = interceptor();

    if (middlewareLogger) {
        middlewareInterceptor.add(middlewareLogger);
    }

    middlewareInterceptor.add(middlewareEffect.middleware);

    const middlewareMap: Partial<IStoreMiddlewareMap> = {
        [IStoreMiddlewareType.Logger]: middlewareLogger,
        [IStoreMiddlewareType.SideEffect]: middlewareEffect,
    } as const;

    const middlewares: IStoreMiddlewares = {
        get: <Type extends keyof IStoreMiddlewareMap, Value = IStoreMiddlewareMap[Type]>(
            type: Type,
        ) => middlewareMap[type] as Value | undefined,
        add: (middleware: Middleware) => {
            middlewareInterceptor.add(middleware);
        },
        remove: (middleware: Middleware) => {
            middlewareInterceptor.remove(middleware);
        },
        all: () => [middlewareInterceptor.middleware()],
    };

    return middlewares;
};

export default buildMiddlewares;
